import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormViewService } from './form-view.service';
import { FormViewCacheService } from './form-view-cache.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { StompMessageService } from '../../../shared/_services/stomp-message.service';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { DrilldownGridComponent, DrilldownTab } from './drilldown-grid/drilldown-grid.component';
import { FormViewDrilldownRoutingService } from './form-view-drilldown-routing.service';
import { AdminGridCallbackEvent } from '../../shared/admin-grid.directive';
import * as _ from 'lodash-es';

@Component({
  selector: 'gtw-form-view',
  templateUrl: './form-view.component.html',
  styleUrls: ['./form-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormViewComponent implements OnInit, OnDestroy {
  private static readonly PENDING_DRILLDOWN_KEY = FormViewDrilldownRoutingService.PENDING_DRILLDOWN_KEY;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();
  @ViewChild('drilldownGrid') drilldownGrid!: DrilldownGridComponent;

  private isInitialized = false;
  private isLoading = false;
  tabWrapperReady = false;
  private workspaceFilterDefs: any[] = [];

  showDrilldown = false;

  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  showFormLoader = true;
  isFilterApplied = false;

  isState = false;
  JCD_KEY = 250;
  SRC = 'FED';
  LEGACY_TAX_YEAR = 2018;

  taxYears: any[] = [];
  taxYear: any = {};
  scenarios: any[] = [];
  scenario: any = {};

  treeTypeTabs: Array<{ name: string; value: string; disabled?: boolean }> = [
    { name: 'Form', value: 'FORM' },
    { name: 'ME', value: 'BSLA' }
  ];
  treeType = 'FORM';

  bslaTypeTabs = [
    { name: 'ALL', value: 'ALL' },
    { name: 'REG', value: 'REG' },
    { name: 'LI', value: 'LI' },
    { name: 'NLI', value: 'NLI' }
  ];
  bslaType = '';

  entityTree: any[] = [];
  entityTreeCopy: any[] = [];
  selectedEntity: any = null;
  selectedEntityBreadcrumb = '';
  loadingEntities = false;

  /**
   * Legacy `ctrl.selectedSubsidiaryType` — tracks which HO variant is active
   * for a given entity (`'All HO' + ein` or `'HO Only' + ein`). Used to drive
   * the active/not-active state on the hover action buttons for Subsidiaries
   * and Parent entity rows.
   */
  selectedSubsidiaryType = '';

  /** True only while the main Filter button request runs: full-screen loader, hide filter + body. */
  filterSubmitFullLoading = false;
  searchTerm = '';
  expandAll = true;
  expandAllTxt = 'Collapse All';

  /** When true, the entity tree sidebar is collapsed (width 0); use close/restore controls. */
  treePaneMinimized = false;

  pdfs: any[] = [];
  selectedForm: any = null;
  loadingForms = false;
  noFormsMsg = 'Please select an entity from the list!';
  formSearchTerm = '';
  isSelectAll = false;
  showSelectAll = false;
  showMerge = false;
  enableMerge = false;
  showExpand = false;

  pdfLoaded = false;
  expandPdfView = true;
  isLoadPdf = false;
  isMerge = false;
  pdfUrl: SafeResourceUrl | null = null;
  pdfLoading = false;
  formNotAvail = false;

  /** `withDrillDowns`: legacy `viewPdf` uses `'1'` for inline iframe, `'0'` when opening via Expand (new tab). */
  pdfParams: any = { systemFlag: 'D', jcdKey: 250, withDrillDowns: '1' };

  canTriggerS3Process = false;
  showUpdateS3Status = false;
  disableS3PushButton = false;
  disableS3UpdateStatus = false;
  enableReviewProcess = false;
  // Legacy `BulkExportModal` (formViewCtrl.js ~L1005) drives its review-process
  // gating from a different config key than the single-PDF view, so keep them
  // separate here as well.
  bulkExportReviewProcess = false;

  irsSubmissionId: string | null = null;
  runningAction = false;

  messageTxt = '';
  messageComplete = false;
  messageWarn = false;
  private completeTimer: any;

  // Watches `document.body` for runtime theme toggles (the header sun/moon icon
  // flips `body.theme-black`/`theme-white` via AppService.applyTheme but does NOT
  // update the cached USER_SETTINGS). Without this, the self-themed shell stays
  // on the stale init-time value and the screen does not switch to dark.
  private themeObserver?: MutationObserver;

  currentTheme = 'theme-black';

  /** Form tile PDF badge: `projects/gtw-elements/src/assets/img/pdf.png` */
  readonly pdfBadgeIconSrc = 'assets/img/pdf.png';

  // Bulk export modal state
  showBulkExport = false;

  // Review diagnostics modal state
  showReviewDiagnostics = false;
  reviewDiagParams: any = {};

  // S3 template view state
  showS3TemplateView = false;
  s3TemplateRowData: any = null;

  // WebSocket
  private readonly WEBSOCKET_ENDPOINT = '/gtw-websockets';
  private readonly WEBSOCKET_CHANNEL = '/notify/form-view/';
  private wsEndpointUrl: string | null = null;
  private wsChannel: string | null = null;

  private drilldownListener: (() => void) | null = null;
  private routerEventsSub: Subscription | null = null;
  /** Last Form Review Angular route (e.g. `/form-view/dashboard?client_key=1`). */
  private drilldownReturnRouterUrl = '';
  /** Avoid duplicate drilldown when replaceState `#/home?...` fires a second hashchange. */
  private lastDrilldownParamSignature = '';

  /** Query keys echoed on the dashboard URL after PDF drilldown (legacy stage parity). */
  private static readonly DRILLDOWN_URL_PARAM_KEYS = [
    'element_name',
    'schedule',
    'line',
    'col_id',
    'line_total',
    'table_name',
    'partNo',
    'part_no'
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private formViewService: FormViewService,
    private cacheService: FormViewCacheService,
    private gtwSnackbarService: GtwSnackbarService,
    private stompMessageService: StompMessageService,
    private appService: AppService,
    private globalServiceProvider: GlobalService,
    private workspaceService: WorkspaceService,
    private route: ActivatedRoute,
    private router: Router,
    private drilldownRouting: FormViewDrilldownRoutingService
  ) {}

  ngOnInit(): void {
    this.initializeFromServices();
    this.tryInit();
  }

  private extractObject(input: any): any {
    if (typeof input === 'string') {
      try {
        const parsed = JSON.parse(input);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      } catch (e) {
        return {};
      }
    }
    return input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  }

  private isStateFormViewContext(routeData: any = this.route.snapshot.data || {}, activeScreen: any = this.workspaceService.workspaceFactory?.activeScreen || {}): boolean {
    if (routeData?.isState === true || routeData?.isState === 'true') {
      return true;
    }

    const candidates = [
      routeData?.screenLink,
      activeScreen?.link,
      activeScreen?.SCREEN_LINK,
      activeScreen?.screen_name,
      this.filterParams?.screen_name,
      this.filterParams?.screenName,
      this.router?.url,
      typeof window !== 'undefined' ? window.location.href : ''
    ]
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value).toLowerCase());

    return candidates.some((value) =>
      value.includes('state-form-view')
      || value.includes('state_form_view')
      || value.includes('state form')
      || /(^|[^a-z])state([^a-z]|$)/.test(value)
    );
  }

  private initializeFromServices(): void {
    const urls = this.extractObject(this.globalServiceProvider.GENERAL_CONFIG || {});
    if (Object.keys(urls).length > 0) {
      this.GENERAL_CONFIG = urls;
    }

    const globalParams = this.extractObject(this.globalServiceProvider.global?.globalParams || {});
    if (Object.keys(globalParams).length > 0) {
      this.globalService = { globalParams };
    }

    this.USER_SETTINGS = this.appService.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.appService.SERVER_CONFIG || {};

    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen || {};
    const filterParams = this.extractObject(
      activeScreen?.filters?.getFilterParams?.() ||
      activeScreen?.filters?.getFilterParamsFromCache?.() ||
      {}
    );

    const hydratedParams = this.globalServiceProvider.hydrateGlobalParamsFromContext(
      activeScreen,
      filterParams
    );
    this.globalService = { globalParams: hydratedParams };

    if (Object.keys(filterParams).length > 0) {
      this.filterParams = filterParams;
    }

    // Keep dropdowns aligned with grid filters by using the same filter definitions.
    const filterDefs = activeScreen?.filters?.getFilters?.();
    if (Array.isArray(filterDefs) && filterDefs.length > 0) {
      this.workspaceFilterDefs = _.cloneDeep(filterDefs);
    }

    // Fallback for direct route navigation (/form-view/dashboard) where workspace filters may be empty.
    const queryParams = this.route.snapshot.queryParams || {};
    if (Object.keys(this.filterParams).length === 0 && Object.keys(queryParams).length > 0) {
      this.filterParams = {
        tax_year: queryParams['tax_year'] || queryParams['taxYear'] || '',
        scenario: queryParams['scenario'] || '',
        screen_name: queryParams['screen_name'] || ''
      };
    }

    const routeData = this.route.snapshot.data || {};
    this.isState = this.isStateFormViewContext(routeData, activeScreen);
  }

  ngOnDestroy(): void {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = undefined;
    }
    if (this.completeTimer) {
      clearTimeout(this.completeTimer);
    }
    if (this.wsEndpointUrl && this.wsChannel) {
      this.stompMessageService.unsubscribeChannel(this.wsEndpointUrl, this.wsChannel);
    }
    if (this.drilldownListener) {
      this.drilldownListener();
      this.drilldownListener = null;
    }
    if (this.routerEventsSub) {
      this.routerEventsSub.unsubscribe();
      this.routerEventsSub = null;
    }
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.GENERAL_CONFIG?.base_url && !this.GENERAL_CONFIG?.pdf_engine_api_url) return;

    this.isInitialized = true;
    this.isLoading = true;
    this.init();
  }

  /**
   * Resolve the theme actually applied to the document. The live
   * `body.theme-black`/`theme-white` class (toggled by the header) is the source
   * of truth; the cached USER_SETTINGS value is only a fallback for the very
   * first paint before the body class is set.
   */
  private resolveCurrentTheme(): string {
    if (typeof document !== 'undefined' && document.body) {
      if (document.body.classList.contains('theme-black')) return 'theme-black';
      if (document.body.classList.contains('theme-white')) return 'theme-white';
    }
    return this.USER_SETTINGS?.user?.selected_theme || 'theme-black';
  }

  /** Keep the self-themed shell in sync with runtime theme toggles. */
  private observeThemeChanges(): void {
    if (this.themeObserver || typeof MutationObserver === 'undefined' || typeof document === 'undefined' || !document.body) {
      return;
    }
    this.themeObserver = new MutationObserver(() => {
      const next = this.resolveCurrentTheme();
      if (next !== this.currentTheme) {
        this.currentTheme = next;
        this.cdr.detectChanges();
      }
    });
    this.themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  private init(): void {
    try {
      this.currentTheme = this.resolveCurrentTheme();
      this.observeThemeChanges();

      this.isState = this.isStateFormViewContext();
      this.JCD_KEY = this.isState ? 253 : 250;
      this.SRC = this.isState ? 'STATE' : 'FED';
      this.pdfParams.jcdKey = this.JCD_KEY;

      this.canTriggerS3Process = this.SERVER_CONFIG?.clientConfig?.pdf_s3_generation_sso_list?.includes(this.USER_SETTINGS?.user?.sso_id) || false;
      this.enableReviewProcess = this.SERVER_CONFIG?.configSettings?.enable_pdf_snapshot_process?.includes(this.USER_SETTINGS?.user?.sso_id)
        || this.SERVER_CONFIG?.configSettings?.enable_pdf_snapshot_process?.toLowerCase() === 'all'
        || false;
      // Bulk-export modal uses `enable_review_process` per legacy
      // BulkExportModal (`formViewCtrl.js` ~L1005), distinct from the
      // single-PDF `enable_pdf_snapshot_process` flag above.
      this.bulkExportReviewProcess = this.SERVER_CONFIG?.configSettings?.enable_review_process?.includes(this.USER_SETTINGS?.user?.sso_id)
        || this.SERVER_CONFIG?.configSettings?.enable_review_process?.toLowerCase() === 'all'
        || false;

      // Keep legacy order parity: shell (index) first, tab body second.
      this.tabWrapperReady = true;
      this.setupFilters();
      // Ensure popup URL rewrite / click capture is active when Form Review mounts
      // (idempotent; also registered from AppComponent for early bootstrap).
      this.drilldownRouting.installGlobalGuards();
      this.captureDrilldownReturnRoute();
      this.setupDrilldownListener();
      this.setupDrilldownRouteSync();
      this.checkInitialUrlForDrilldown();
      this.establishWebSocketConnection();
      // Restore cached state AFTER filters are wired up so dropdown selections
      // align with the cached tree/forms/PDF (legacy parity with `loadCache()`
      // in `formViewCtrl.js`).
      this.restoreFromCache();
      this.restoreDrilldownTabsFromCache();
      this.consumePendingDrilldown();
      this.syncDrilldownRouteFromUrl();
      this.showFormLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in FormView init:', e);
      this.tabWrapperReady = true;
      this.showFormLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private setupDrilldownListener(): void {
    if (this.drilldownListener) return;

    this.drilldownRouting.installGlobalGuards();

    const handler = (event: HashChangeEvent) => {
      const newUrl = event.newURL || window.location.href || '';
      this.processDrilldownPopupFromUrl(newUrl, event.oldURL || '');
    };

    window.addEventListener('hashchange', handler);
    this.drilldownListener = () => window.removeEventListener('hashchange', handler);
  }

  private parseDrilldownParamsFromUrl(url: string): Record<string, string> | null {
    return this.drilldownRouting.parseDrilldownParamsFromUrl(url);
  }

  /** Legacy `formViewCtrl` maps URL `null` to empty; store clean values for tab header. */
  private normalizeDrilldownPopupValue(value: string | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    const raw = String(value).trim();
    if (!raw || raw.toLowerCase() === 'null' || raw.toLowerCase() === 'undefined') {
      return '';
    }
    return raw;
  }

  private getDefaultFormViewRoutePath(): string {
    return this.drilldownRouting.getDefaultFormViewRoutePath(this.isState);
  }

  private sanitizeDrilldownReturnRoute(route: string): string {
    return this.drilldownRouting.sanitizeDrilldownReturnRoute(route, this.isState);
  }

  private buildFormViewUrlWithDrilldownParams(drilldownParams?: Record<string, string>): string {
    return this.drilldownRouting.buildFormViewUrlWithDrilldownParams(
      drilldownParams || {},
      { returnRoute: this.drilldownReturnRouterUrl, isState: this.isState }
    );
  }

  /** Entity/form context for drilldown — live selection or Form View cache after remount. */
  private resolveEntityForDrilldown(): any | null {
    if (this.selectedEntity) {
      return this.selectedEntity;
    }
    const cached = this.cacheService.getBucket(this.isState).selectedEntity;
    if (cached) {
      this.setSelectedEntity(cached);
      return cached;
    }
    return null;
  }

  private resolveFormForDrilldown(): any | null {
    if (this.selectedForm) {
      return this.selectedForm;
    }
    return this.cacheService.getBucket(this.isState).selectedForm || null;
  }

  private setupDrilldownRouteSync(): void {
    if (this.routerEventsSub) {
      return;
    }
    this.routerEventsSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.syncDrilldownRouteFromUrl();
        this.onDrilldownWorkspaceChange();
      });
  }

  /**
   * Legacy `form-view.element-view` — URL carries tab code (`AL8301-IRS1065.OwedAmt`).
   * Activate the matching drilldown tab when the route changes.
   */
  private syncDrilldownRouteFromUrl(): void {
    const tabId = this.drilldownRouting.parseElementViewTabId(this.router.url || '', this.isState);
    if (!tabId) {
      if (this.drilldownGrid && !this.drilldownGrid.formReviewActive && this.drilldownGrid.tabs.length === 0) {
        this.showDrilldown = false;
        this.cdr.detectChanges();
      }
      return;
    }

    if (!this.drilldownGrid) {
      return;
    }

    let tab = this.drilldownGrid.tabs.find(t => t.code === tabId);
    if (!tab) {
      const cached = this.cacheService.getDrilldownTabs(this.isState).find((d: any) => {
        const code = this.drilldownRouting.buildTabCode(d?.leName, d?.elementName);
        return code === tabId;
      });
      if (cached) {
        this.drilldownGrid.addTab(cached);
        tab = this.drilldownGrid.tabs.find(t => t.code === tabId);
      }
    }

    if (tab) {
      this.showDrilldown = true;
      this.drilldownGrid.activateTab(tab);
      this.onDrilldownWorkspaceChange();
    }
  }

  private navigateToElementViewTab(tabCode: string): void {
    if (!tabCode) {
      return;
    }
    const currentTabId = this.drilldownRouting.parseElementViewTabId(
      this.router.url || '',
      this.isState
    );
    if (currentTabId === tabCode) {
      this.syncDrilldownRouteFromUrl();
      return;
    }
    void this.router.navigateByUrl(
      this.drilldownRouting.createElementViewUrlTree(tabCode, this.isState)
    );
  }

  private navigateToFormViewDashboard(): void {
    const tree = this.drilldownRouting.createDashboardUrlTree(this.isState);
    void this.router.navigateByUrl(tree);
  }

  private persistDrilldownTabs(): void {
    if (!this.drilldownGrid) {
      return;
    }
    const tabsData = this.drilldownGrid.tabs.map(t => t.data);
    this.cacheService.saveDrilldownTabs(this.isState, tabsData);
  }

  private restoreDrilldownTabsFromCache(): void {
    const cached = this.cacheService.getDrilldownTabs(this.isState);
    if (!cached.length || !this.drilldownGrid) {
      return;
    }
    cached.forEach((data: any) => {
      const code = this.drilldownRouting.buildTabCode(data?.leName, data?.elementName);
      if (!this.drilldownGrid!.tabs.some(t => t.code === code)) {
        this.drilldownGrid!.addTab(data);
      }
    });
    this.onDrilldownWorkspaceChange();
  }

  private isOnFormViewRouterPath(): boolean {
    const path = (this.router.url || '').split('?')[0].toLowerCase();
    return path.includes('/form-view') || path.includes('/state-form-view');
  }

  /**
   * Legacy `form-view.dashboard` vs `form-view.element-view`: element-view loads only
   * `form-view-tab.html` (drilldown grid) — not tree/PDF (`form-view.html`).
   */
  get formReviewPanesVisible(): boolean {
    const onElementView = !!this.drilldownRouting.parseElementViewTabId(
      this.router.url || '',
      this.isState
    );
    if (onElementView) {
      return false;
    }
    const grid = this.drilldownGrid;
    if (grid && !grid.formReviewActive && grid.activeTab) {
      return false;
    }
    return true;
  }

  /** Keep OnPush parent in sync when drilldown child toggles tree/PDF visibility. */
  onDrilldownWorkspaceChange(): void {
    this.showDrilldown = !this.formReviewPanesVisible;
    this.cdr.markForCheck();
  }

  /** Tab bar list — survives PDF Forms switch (legacy `vm.tabList` is not cleared). */
  get drilldownTabsForBar(): DrilldownTab[] {
    const live = this.drilldownGrid?.tabs || [];
    if (live.length > 0) {
      return live;
    }
    return this.cacheService.getDrilldownTabs(this.isState).map((data: any) => {
      const code = this.drilldownRouting.buildTabCode(data?.leName, data?.elementName);
      return {
        code,
        name: data?.leName || code,
        data,
        active: false,
        loading: false,
        cached: true,
        gridConfig: { actionId: 0, gridId: '', extraParams: {}, autoLoad: false },
        gridParams: {},
      } as DrilldownTab;
    });
  }

  private processDrilldownPopupFromUrl(popupUrl: string, previousUrl: string): void {
    const params = this.parseDrilldownParamsFromUrl(popupUrl);
    if (!params) {
      return;
    }

    const signature = JSON.stringify(params);
    if (signature === this.lastDrilldownParamSignature) {
      return;
    }
    this.lastDrilldownParamSignature = signature;

    this.rememberDrilldownReturnRouteFromUrl(previousUrl);
    // Survive Angular hash routing: `#popup` can remount this CE before drilldown runs.
    this.queuePendingDrilldown(params);
    this.restoreAppRouteAfterPopup();

    if (this.resolveEntityForDrilldown()) {
      this.runDrilldownRequest(params);
    }
  }

  private queuePendingDrilldown(params: Record<string, string>): void {
    this.drilldownRouting.queuePendingDrilldown(params, {
      returnRoute: this.drilldownReturnRouterUrl,
      isState: this.isState,
    });
  }

  private consumePendingDrilldown(): void {
    const pending = this.drilldownRouting.readPendingDrilldown();
    if (!pending?.params) {
      return;
    }

    if (pending.returnRoute) {
      this.drilldownReturnRouterUrl = pending.returnRoute;
    }
    if (pending.isState === true) {
      this.isState = true;
      this.JCD_KEY = 253;
      this.SRC = 'STATE';
    }

    if (this.resolveEntityForDrilldown()) {
      this.runDrilldownRequest(pending.params);
    }
  }

  private runDrilldownRequest(params: Record<string, string>): void {
    const entity = this.resolveEntityForDrilldown();
    if (!entity) {
      return;
    }

    this.drilldownRouting.clearPendingDrilldown();

    const baseUrl = this.GENERAL_CONFIG?.base_url || '';
    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';
    const selectedForm = this.resolveFormForDrilldown();

    this.formViewService.getCombinationKeysByEntity(
      baseUrl, ssoId,
      this.getTaxYearValue(),
      this.getScenarioValue(),
      this.JCD_KEY,
      entity.leid || '',
      entity.cdr_no || '',
      entity.reporting_period || '',
      entity.le_type || ''
    ).subscribe(
      (resp: any) => {
        if (resp?.callSuccess === '1' && resp?.jsonObject) {
          const combinationKeys = resp.jsonObject.map((k: any) => k.COMBINATION_KEY).join(',');
          const drilldownData = {
            leName: entity.name,
            sso: ssoId,
            taxYear: this.getTaxYearValue(),
            scenario: this.getScenarioValue(),
            jcdKey: this.JCD_KEY,
            sourceSystem: entity.source_system_flag || 'D',
            summaryLevel: entity.le_type || '',
            meCodes: entity.le_type === 'BSLA' ? entity.leid : '',
            combinationKeys,
            form: selectedForm?.form_name || '',
            schedule: this.normalizeDrilldownPopupValue(params['schedule']),
            partNo: this.normalizeDrilldownPopupValue(params['partNo'] || params['part_no']),
            line: this.normalizeDrilldownPopupValue(params['line']),
            colId: this.normalizeDrilldownPopupValue(params['col_id']),
            lineTotal: this.normalizeDrilldownPopupValue(params['line_total']),
            elementName: params['element_name'] || ''
          };
          this.openDrilldown(drilldownData);
        }
      },
      (_err: any) => {
        this.gtwSnackbarService.openSnackBar('Error retrieving combination keys for selected entity!', 'gtw-snackbar--red');
      }
    );
  }

  /** Remember current Form Review route while the user is on PDF Forms. */
  private captureDrilldownReturnRoute(): void {
    const url = (this.router.url || '').trim();
    if (url && !url.toLowerCase().includes('popup')) {
      this.drilldownReturnRouterUrl = this.sanitizeDrilldownReturnRoute(
        url.startsWith('/') ? url : `/${url}`
      );
    }
  }

  private rememberDrilldownReturnRouteFromUrl(fullUrl: string): void {
    if (!fullUrl) {
      return;
    }
    const hashIdx = fullUrl.indexOf('#/');
    if (hashIdx < 0) {
      return;
    }
    const route = fullUrl.substring(hashIdx + 1).split('?')[0];
    if (route && !route.toLowerCase().includes('popup')) {
      this.drilldownReturnRouterUrl = this.sanitizeDrilldownReturnRoute(
        route.startsWith('/') ? route : `/${route}`
      );
    }
  }

  /** Handle drilldown when the page loads with params already in the hash (no hashchange). */
  private checkInitialUrlForDrilldown(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const href = window.location.href || '';
    const params = this.parseDrilldownParamsFromUrl(href);
    if (!params) {
      return;
    }
    this.drilldownReturnRouterUrl = this.getDefaultFormViewRoutePath();

    const runDrilldown = (): void => {
      this.processDrilldownPopupFromUrl(href, href);
    };

    if (!this.isOnFormViewRouterPath()) {
      void this.router
        .navigateByUrl(this.drilldownRouting.createDashboardUrlTree(this.isState), { replaceUrl: true })
        .then(runDrilldown);
      return;
    }

    runDrilldown();
  }

  /**
   * After `#popup?...`, ensure Form Review is mounted (legacy stayed on current state).
   * Element-view URL is set only after the drilldown tab opens.
   */
  private restoreAppRouteAfterPopup(): void {
    if (this.isOnFormViewRouterPath()) {
      return;
    }
    void this.router.navigateByUrl(
      this.drilldownRouting.createDashboardUrlTree(this.isState),
      { replaceUrl: true }
    );
  }

  private setupFilters(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen || {};
    const liveFilterDefs = activeScreen?.filters?.getFilters?.();
    if (Array.isArray(liveFilterDefs) && liveFilterDefs.length > 0) {
      this.workspaceFilterDefs = _.cloneDeep(liveFilterDefs);
    }

    // Legacy `FormViewCtrl` (`formViewCtrl.js` L60-71) prefers `cacheStore.filters`
    // if available so the dropdowns survive cross-screen navigation.
    const cachedFilters = this.cacheService.getBucket(this.isState).filters || {};

    // Primary source: same filter model used by workspace grid filters.
    const taxYearFilter = this.findFilterByParamNames(['tax_year', 'taxyear']);
    const scenarioFilter = this.findFilterByParamNames(['scenario', 'scenario_key']);

    const taxYearValues = this.getFilterValues(taxYearFilter);
    if (taxYearValues.length > 0) {
      this.taxYears = taxYearValues;
      const taxYearValueName = this.getFilterValueName(taxYearFilter);
      const selectedTaxYear = this.getSingleSelected(taxYearFilter);
      const cachedTaxYearValue = this.getTaxYearValueFromOption(cachedFilters?.taxYear);
      const selectedTaxYearValue =
        cachedTaxYearValue
        || selectedTaxYear?.[taxYearValueName]
        || selectedTaxYear?.VALUE
        || selectedTaxYear?.value
        || selectedTaxYear?.label
        || this.filterParams?.tax_year;

      this.taxYear = this.taxYears.find((ty: any) =>
        String(ty?.[taxYearValueName] ?? ty?.VALUE ?? ty?.value ?? ty?.label ?? '') === String(selectedTaxYearValue ?? '')
      ) || this.taxYears[0] || {};

      const scenarioValues = this.getFilterValues(scenarioFilter);
      if (scenarioValues.length > 0) {
        this.scenarios = scenarioValues;
        const scenarioValueName = this.getFilterValueName(scenarioFilter);
        const selectedScenario = this.getSingleSelected(scenarioFilter);
        const cachedScenarioValue = String(
          cachedFilters?.scenario?.scenario
          ?? cachedFilters?.scenario?.[scenarioValueName]
          ?? cachedFilters?.scenario?.VALUE
          ?? cachedFilters?.scenario?.value
          ?? ''
        );
        const selectedScenarioValue =
          (cachedScenarioValue && cachedScenarioValue.length > 0 ? cachedScenarioValue : null)
          ?? selectedScenario?.[scenarioValueName]
          ?? selectedScenario?.scenario
          ?? selectedScenario?.VALUE
          ?? selectedScenario?.value
          ?? this.filterParams?.scenario;

        this.scenario = this.scenarios.find((s: any) =>
          String(s?.[scenarioValueName] ?? s?.scenario ?? s?.VALUE ?? s?.value ?? '') === String(selectedScenarioValue ?? '')
        ) || this.scenarios.find((s: any) => s?.default_scenario === 'Y') || this.scenarios[0] || {};
      } else {
        this.updateScenarios();
      }
      return;
    }

    // Secondary source: global inputs used by header/workspace filters.
    const globalTaxYearValues = this.globalServiceProvider?.global?.inputs?.tax_year?.values || [];
    const gp = this.globalService?.globalParams || {};
    const globalTaxYearValuesFallback = gp?.tax_year_values || [];

    if (globalTaxYearValues.length > 0 || globalTaxYearValuesFallback.length > 0) {
      this.taxYears = _.cloneDeep(globalTaxYearValues.length > 0 ? globalTaxYearValues : globalTaxYearValuesFallback);
      const cachedTaxYearValue = this.getTaxYearValueFromOption(cachedFilters?.taxYear);
      const selectedTaxYear = cachedTaxYearValue
        || this.filterParams?.tax_year
        || gp?.tax_year
        || this.globalServiceProvider?.global?.inputs?.tax_year?.selected?.value;
      this.taxYear = this.taxYears.find((ty: any) =>
        String(this.getTaxYearValueFromOption(ty)) === String(selectedTaxYear ?? '')
      ) || this.taxYears[0] || {};
    } else {
      this.taxYear = {
        label: this.filterParams?.tax_year || gp?.tax_year || '',
        value: this.filterParams?.tax_year || gp?.tax_year || '',
        scenarios: []
      };
      this.taxYears = [this.taxYear];
    }

    this.updateScenarios();

    // After scenarios are computed, re-align with cached scenario if possible
    // (covers fallback path where workspace filter defs are not loaded).
    if (cachedFilters?.scenario && this.scenarios.length > 0) {
      const cachedScenarioCode = String(
        cachedFilters.scenario?.scenario
        ?? cachedFilters.scenario?.VALUE
        ?? cachedFilters.scenario?.value
        ?? ''
      );
      if (cachedScenarioCode.length > 0) {
        const matched = this.scenarios.find((s: any) =>
          String(s?.scenario ?? s?.VALUE ?? s?.value ?? '') === cachedScenarioCode
        );
        if (matched) this.scenario = matched;
      }
    }
  }

  private updateScenarios(): void {
    if (this.taxYear?.scenarios) {
      this.scenarios = this.taxYear.scenarios.filter((s: any) => s.jcd_key == this.JCD_KEY);
    } else {
      this.scenarios = [];
    }
    this.scenario = this.scenarios.find((s: any) => s.default_scenario === 'Y')
      || this.scenarios[0]
      || { scenario: this.filterParams?.scenario || '' };
  }

  private isLocalRuntime(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const host = (window.location?.hostname || '').toLowerCase();
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
  }

  private normalizeUrlForCurrentOrigin(url: string): string {
    const rawUrl = (url || '').trim();
    if (!rawUrl || !this.isLocalRuntime() || typeof window === 'undefined') {
      return rawUrl;
    }

    try {
      const parsed = new URL(rawUrl, window.location.origin);
      const isAbsoluteHttp = /^https?:\/\//i.test(rawUrl);
      if (!isAbsoluteHttp || parsed.origin === window.location.origin) {
        return rawUrl;
      }
      return `${window.location.origin}${parsed.pathname}${parsed.search}`;
    } catch (e) {
      return rawUrl;
    }
  }

  get pdfEngineUrl(): string {
    return this.normalizeUrlForCurrentOrigin(this.GENERAL_CONFIG?.pdf_engine_api_url || '');
  }

  get bulkPdfUrl(): string {
    return this.normalizeUrlForCurrentOrigin(this.GENERAL_CONFIG?.bulk_pdf_engine_api_url || '');
  }

  get baseUrl(): string {
    return this.normalizeUrlForCurrentOrigin(this.GENERAL_CONFIG?.base_url || '');
  }

  changeTaxYear(): void {
    this.updateScenarios();
    this.filterParams = {
      ...(this.filterParams || {}),
      tax_year: this.getTaxYearValue(),
      scenario: this.getScenarioValue()
    };
    // Legacy `formViewCtrl.js` L149-156: `cacheStore.filters.taxYear/scenario` is
    // updated on every dropdown change so the next entry into the screen
    // restores the same selection.
    this.cacheService.saveFilters(this.isState, this.taxYear, this.scenario);
    this.cdr.detectChanges();
  }

  changeScenario(): void {
    this.filterParams = {
      ...(this.filterParams || {}),
      scenario: this.getScenarioValue()
    };
    this.cacheService.saveFilters(this.isState, this.taxYear, this.scenario);
    this.cdr.detectChanges();
  }

  /**
   * Legacy parity: `FormViewCtrl.loadCache()` (`formViewCtrl.js` L219-238).
   * Re-applies the previously cached entity tree, PDF list, selected entity,
   * selected form, and PDF blob URL when the user returns to Form View from
   * another route (for example `/workspace/bulk-export-history`).
   */
  private restoreFromCache(): void {
    const bucket = this.cacheService.getBucket(this.isState);

    if (!bucket || (!bucket.isFilterApplied && !bucket.selectedEntity && !Object.keys(bucket.entityTreeCopy).length)) {
      return;
    }

    if (bucket.treeType) this.treeType = bucket.treeType;
    if (bucket.bslaType !== undefined) this.bslaType = bucket.bslaType;

    const cachedTree = bucket.entityTreeCopy[`${this.treeType}_${this.bslaType}`];
    if (cachedTree && cachedTree.length > 0) {
      this.entityTree = cachedTree;
      this.entityTreeCopy = _.cloneDeep(cachedTree);
      this.isFilterApplied = true;
    }

    if (bucket.pdfs && bucket.pdfs.length > 0) {
      this.pdfs = bucket.pdfs;
      this.noFormsMsg = '';
    }

    if (bucket.selectedEntity) {
      this.setSelectedEntity(bucket.selectedEntity);
      this.setEntityLevelPdfParams();
      this.showSelectAll = this.showMerge = this.pdfs.length > 0;
    }

    if (bucket.pdfParams) {
      // Merge but preserve `withDrillDowns` default for inline iframe.
      this.pdfParams = { ...this.pdfParams, ...bucket.pdfParams };
    }

    if (bucket.selectedForm) {
      this.selectedForm = bucket.selectedForm;
      this.showExpand = true;
    }

    if (bucket.pdfBlobUrl) {
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(bucket.pdfBlobUrl);
      this.isLoadPdf = true;
      this.expandPdfView = false;
      this.pdfLoaded = true;
    }
  }

  getTaxYearLabel(option: any): string {
    return String(option?.label ?? option?.NAME ?? option?.name ?? option?.LABEL ?? option?.value ?? option?.VALUE ?? '');
  }

  getScenarioLabel(option: any): string {
    return String(option?.scenario_desc ?? option?.label ?? option?.NAME ?? option?.name ?? option?.LABEL ?? option?.scenario ?? option?.value ?? option?.VALUE ?? '');
  }

  private findFilterByParamNames(paramNames: string[]): any {
    const normalized = paramNames.map((p) => p.toLowerCase());
    return this.workspaceFilterDefs.find((f: any) => normalized.includes(String(f?.param_name || '').toLowerCase()));
  }

  private getFilterValues(filter: any): any[] {
    return Array.isArray(filter?.values) ? _.cloneDeep(filter.values) : [];
  }

  private getSingleSelected(filter: any): any {
    if (!filter?.selected) return null;
    if (Array.isArray(filter.selected)) {
      return filter.selected.length > 0 ? filter.selected[0] : null;
    }
    return filter.selected;
  }

  private getFilterValueName(filter: any): string {
    return filter?.value_name || 'VALUE';
  }

  private getTaxYearValueFromOption(option: any): string {
    return String(option?.value ?? option?.VALUE ?? option?.label ?? option?.NAME ?? '');
  }

  private getTaxYearValue(): string {
    return this.getTaxYearValueFromOption(this.taxYear) || String(this.filterParams?.tax_year ?? '');
  }

  private getScenarioValue(): string {
    return String(this.scenario?.scenario ?? this.scenario?.value ?? this.scenario?.VALUE ?? this.filterParams?.scenario ?? '');
  }

  /**
   * Legacy `index.html` PDF Forms: `formReviewActive = true` + `form-view.dashboard`.
   * Drilldown tabs remain in `tabList`; only the ui-view body switches to tree/PDF.
   */
  goToFormReviewTab(): void {
    if (this.drilldownGrid) {
      this.drilldownGrid.showFormReview();
      this.persistDrilldownTabs();
    }
    this.showDrilldown = false;
    void this.router.navigateByUrl(
      this.drilldownRouting.createDashboardUrlTree(this.isState)
    );
    this.onDrilldownWorkspaceChange();
  }

  /** After Bulk Export History (or any other workspace tab), restore PDF Forms URL + active screen. */
  private navigateToPdfFormsWorkspaceScreen(): void {
    const candidates = ['workspace.form-view.dashboard', 'form-view.dashboard'];
    let found: { activity: any; screen: any } | null = null;
    for (const link of candidates) {
      const prefixed = link.startsWith('workspace.') ? link : `workspace.${link}`;
      found =
        this.workspaceService.findScreenByLink(prefixed)
        || this.workspaceService.findScreenByLink(link);
      if (found) {
        break;
      }
    }
    if (!found?.activity || !found?.screen) {
      this.navigateToFormViewDashboard();
      return;
    }
    this.workspaceService.setActivity(found.activity, true, found.screen);
    // Helper accounts for top-level legacy states (form-view, …) that
    // live OUTSIDE `/workspace/`. See WorkspaceService.
    const commands = this.workspaceService.getScreenNavigationCommands(found.screen);
    const clientKey = sessionStorage.getItem('client_key') || '0';
    this.router.navigate(commands, {
      queryParams: { client_key: clientKey },
    });
  }

  openBulkExportHistory(): void {
    const candidateLinks = this.isState
      ? ['workspace.state-bulk-export-history', 'state-bulk-export-history']
      : ['workspace.bulk-export-history', 'bulk-export-history'];

    let found: { activity: any; screen: any } | null = null;
    for (const link of candidateLinks) {
      found = this.workspaceService.findScreenByLink(link);
      if (found) {
        break;
      }
    }

    if (!found?.activity || !found?.screen) {
      this.gtwSnackbarService.openSnackBar('Bulk export history screen is not available.', 'gtw-snackbar--yellow');
      return;
    }

    this.workspaceService.setActivity(found.activity, true, found.screen);
    // Helper accounts for top-level legacy states (form-view, …) that
    // live OUTSIDE `/workspace/`. See WorkspaceService.
    const commands = this.workspaceService.getScreenNavigationCommands(found.screen);
    const clientKey = sessionStorage.getItem('client_key') || '0';
    this.router.navigate(commands, {
      queryParams: { client_key: clientKey },
    });
  }

  activateDrilldownTab(tab: DrilldownTab): void {
    if (!tab || !this.drilldownGrid) {
      return;
    }
    let target = this.drilldownGrid.tabs.find(t => t.code === tab.code);
    if (!target && tab.data) {
      this.drilldownGrid.addTab(tab.data);
      target = this.drilldownGrid.tabs.find(t => t.code === tab.code);
    }
    if (!target) {
      return;
    }
    this.showDrilldown = true;
    this.drilldownGrid.activateTab(target);
    this.navigateToElementViewTab(target.code);
    this.onDrilldownWorkspaceChange();
  }

  closeDrilldownTab(tab: any, event: Event): void {
    if (!tab || !this.drilldownGrid) {
      return;
    }
    const wasLastTab = this.drilldownGrid.tabs.length === 1 && tab.active;
    this.drilldownGrid.closeTab(tab, event);
    this.persistDrilldownTabs();
    if (wasLastTab || this.drilldownGrid.tabs.length === 0) {
      this.navigateToFormViewDashboard();
    } else if (this.drilldownGrid.activeTab) {
      this.navigateToElementViewTab(this.drilldownGrid.activeTab.code);
    }
    this.cdr.detectChanges();
  }

  loadHierarchy(treeTypeTab?: any, filterClicked = false, filterSubmitFullScreen = false): void {
    if (treeTypeTab && treeTypeTab.disabled) return;

    this.treeType = treeTypeTab ? treeTypeTab.value : (this.treeType || 'FORM');

    if (this.treeType === 'FORM') {
      this.bslaType = '';
    } else if (this.bslaType === '') {
      this.bslaType = 'ALL';
    }

    if (!this.pdfEngineUrl) {
      this.gtwSnackbarService.openSnackBar('PDF engine URL not configured', 'gtw-snackbar--red');
      return;
    }

    // Legacy parity: clicking Filter invalidates downstream cached data because
    // the filter selection may have changed (`formViewCtrl.js` L255-275).
    if (filterClicked) {
      this.cacheService.clearDownstream(this.isState);
      this.cacheService.saveFilters(this.isState, this.taxYear, this.scenario);
      this.cacheService.setFilterApplied(this.isState, true);
      this.pdfs = [];
      this.selectedForm = null;
      this.selectedEntity = null;
      this.pdfUrl = null;
      this.isLoadPdf = false;
      this.isFilterApplied = true;
      this.treePaneMinimized = false;
    } else {
      // Legacy: when switching tree type (Form/ME) without re-filtering, the
      // factory returns the cached hierarchy bucket without re-querying.
      const cachedTree = this.cacheService.getHierarchy(this.isState, this.treeType, this.bslaType);
      if (cachedTree && cachedTree.length > 1) {
        this.entityTree = cachedTree;
        this.entityTreeCopy = _.cloneDeep(this.entityTree);
        this.pdfs = [];
        this.noFormsMsg = 'Please select an entity from the list!';
        this.cdr.detectChanges();
        return;
      }
    }

    if (filterClicked && filterSubmitFullScreen) {
      this.filterSubmitFullLoading = true;
    }

    this.loadingEntities = true;
    this.noFormsMsg = '';
    this.pdfLoaded = false;
    this.entityTree = [];
    this.entityTreeCopy = [];
    this.cdr.detectChanges();

    this.formViewService.loadHierarchy(
      this.pdfEngineUrl, this.treeType, this.bslaType,
      this.getTaxYearValue(), this.getScenarioValue(),
      this.JCD_KEY, this.isState
    ).subscribe(
      (response: any) => {
        if (filterClicked) {
          this.isFilterApplied = true;
        }
        this.loadingEntities = false;
        this.expandPdfView = true;
        let list = response?.viewList;

        if (!list) {
          list = [{ name: 'Something went wrong!', noDataFlag: true }];
        } else if (list.length === 0) {
          list = [{ name: 'No data available!', noDataFlag: true }];
        }

        this.entityTree = list;
        if (this.treeType !== 'BSLA') this.sanitizeTree();
        this.entityTreeCopy = _.cloneDeep(this.entityTree);
        this.cacheService.saveHierarchy(this.isState, this.treeType, this.bslaType, _.cloneDeep(this.entityTree));

        this.noFormsMsg = 'Please select an entity from the list!';
        this.pdfs = [];

        if (this.treeType === 'FORM') this.expandCollapseAll();
        this.filterSubmitFullLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (filterClicked) {
          this.isFilterApplied = true;
        }
        console.error('Error loading hierarchy:', error);
        this.entityTree = [{ name: 'Something went wrong!', noDataFlag: true }];
        this.loadingEntities = false;
        this.expandPdfView = true;
        this.filterSubmitFullLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  entitySelected(entity: any): void {
    this.selectedForm = null;
    this.cacheService.saveSelectedForm(this.isState, null);
    this.cacheService.savePdfBlob(this.isState, null, null);
    this.resetFlags();
    this.setSelectedEntity(entity);
    this.setEntityLevelPdfParams();
    this.getForms(entity, 'N');
  }

  private resetFlags(): void {
    this.isSelectAll = this.pdfs && this.pdfs.length > 0 && this.getSelectedPDFs().length === this.pdfs.length;
    this.showSelectAll = this.showMerge = this.pdfs && this.pdfs.length > 0;
    this.enableMerge = this.getSelectedPDFs().length > 1;
    this.showExpand = false;
    this.formSearchTerm = '';
    this.expandPdfView = true;
    this.messageTxt = '';
    this.messageComplete = false;
    this.pdfLoaded = false;
    this.showUpdateS3Status = false;
  }

  private setSelectedEntity(entity: any): void {
    this.selectedEntity = entity;
    if (entity?.path) {
      this.selectedEntityBreadcrumb = entity.path.replace(/\/ /g, ' -> ');
      this.selectedEntityBreadcrumb = this.selectedEntityBreadcrumb.replace(
        entity.leid, entity.le_type + ' -> ' + entity.leid
      );
    }
    this.cacheService.saveSelectedEntity(this.isState, entity);
    this.loadIRSId();
  }

  private setEntityLevelPdfParams(): void {
    const e = this.selectedEntity;
    this.pdfParams.leId = e.leid;
    this.pdfParams.cdrNo = e.cdr_no || '';
    this.pdfParams.rptPeriod = e.reporting_period || '';
    this.pdfParams.leType = e.le_type;
    this.pdfParams.selectedEntityType = e.entity_type;
    this.pdfParams.selectedConsolGroup = e.consol_group_key;
    this.pdfParams.keyId = e.le_type === 'HOO' ? e.hoo_key_id : e.key_id;
    this.pdfParams.submission_id = e.le_type === 'HOO' ? e.hoo_submission_id : e.submission_id;
    this.pdfParams.leName = e.name;
    this.pdfParams.returnType = e.return_type;
    this.pdfParams.consolType = e.consol_type;
    this.pdfParams.sourceSystemFlag = e.source_system_flag || 'D';
    this.pdfParams.taxYear = e.tax_year;
    this.pdfParams.scenarioLocal = e.scenario;
  }

  private getForms(entity: any, isAutoReload: string): void {
    this.pdfs = [];
    this.enableMerge = false;
    this.isSelectAll = false;
    this.loadingForms = true;
    this.noFormsMsg = '';
    this.cdr.detectChanges();

    const selectedView = this.resolveSelectedView(entity);
    const businessType = this.resolveBusinessType(entity);
    const entityType = (this.treeType === 'BSLA' ? this.bslaType : entity.entity_type) || '';
    // Legacy parity: getPdfTaxForms consumed entity tax/scenario first.
    const taxYear = entity.tax_year || this.getTaxYearValue() || '';
    const scenario = entity.scenario || this.getScenarioValue() || '';
    const keyId = this.resolveKeyId(entity);
    const submissionId = this.resolveSubmissionId(entity);
    const cdrNo = this.nullableParam(entity.cdr_no);
    const reportingPeriod = this.nullableParam(entity.reporting_period);

    this.formViewService.getTaxForms(
      this.pdfEngineUrl, selectedView, reportingPeriod, entity.le_type || 'HO',
      cdrNo, entity.source_system_flag || 'D', businessType, entityType,
      entity.consol_group_key, taxYear, scenario, entity.name,
      entity.path, this.SRC, entity.consol_group_key, 'GTW',
      keyId,
      submissionId,
      entity.return_type || '', entity.consol_type || '', isAutoReload, this.JCD_KEY
    ).subscribe(
      (response: any) => {
        this.loadingForms = false;

        if (response.callSuccess === '1' && response.message?.length > 0) {
          this.noFormsMsg = response.message;
          this.cdr.detectChanges();
          return;
        }

        if (isAutoReload !== 'Y') this.expandPdfView = true;
        this.showSelectAll = this.showMerge = true;
        this.showUpdateS3Status = this.SERVER_CONFIG?.clientConfig?.pdf_s3_generation_sso_list?.includes(this.USER_SETTINGS?.user?.sso_id) || false;

        if (response.taxForms && response.taxForms.length === 0) {
          this.noFormsMsg = 'No Forms Available';
          this.showSelectAll = false;
          this.showMerge = false;
          this.cdr.detectChanges();
          return;
        }

        const formTypes = this.parseTaxForms(response);
        if (formTypes === null) {
          this.expandPdfView = true;
          this.noFormsMsg = 'PDFs not available at this time. The Global Tax support team has been notified.';
          this.showSelectAll = false;
          this.showMerge = false;
          this.cdr.detectChanges();
          return;
        }

        if (formTypes.length === 0) {
          this.noFormsMsg = 'No Forms Available';
          this.showSelectAll = false;
          this.showMerge = false;
          this.cdr.detectChanges();
          return;
        }

        this.noFormsMsg = '';
        this.selectedEntity.batchId = response.batchId;

        formTypes.forEach((formType: any) => {
          const typeName = Object.keys(formType)[0];
          const subTypes = formType[typeName];
          subTypes.forEach((subType: any) => {
            const forms = subType['attachment_form'];
            forms.forEach((form: any) => {
              form.checked = false;
              form.label = form.irs_form && form.bucket_sub !== '5471'
                ? form.irs_form + '-Sch_' + form.schedule + '-' + form.line_no + ' ' + form.form_name
                : (form.form_name.toLowerCase().includes('schedule')
                  ? form.form_name.replace('Schedule', '-') : form.form_name);
              form.label = form.subform && form.form_sub_cat === 'DIFF_PDF'
                ? form.label + '-' + form.subform : form.label;
              form.form_sub_cat === 'SAME_PDF' && (form.subform = null);
              form.form_order = Number(form.form_order);
              form.isDisplay = true;
              this.pdfs.push(form);
            });
          });
        });

        this.pdfs = _.sortBy(_.uniqBy(this.pdfs, 'label'), 'form_order');

        if (this.selectedEntity.consol_type === 'PSHIP' && this.pdfParams.keyId === 0 && this.pdfs[0]) {
          this.pdfParams.keyId = this.pdfs[0].key_id;
        }

        // Cache the resolved PDF list so we don't re-fetch on cross-route return.
        this.cacheService.savePdfs(this.isState, this.pdfs);
        this.cacheService.saveSelectedEntity(this.isState, this.selectedEntity);

        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loadingForms = false;
        this.expandPdfView = true;
        this.noFormsMsg = 'PDFs not available at this time. The Global Tax support team has been notified.';
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * BSLA rows can carry either `me_label` or `leid` depending on hierarchy node.
   * Avoid sending literal "null"/undefined to `selectedView`.
   */
  private resolveSelectedView(entity: any): string {
    // Legacy-effective behavior in formViewCtrl used LEID as selectedView.
    const raw = entity?.leid ?? entity?.me_label ?? entity?.name ?? '';
    const s = String(raw ?? '').trim();
    return s === 'null' || s === 'undefined' ? '' : s;
  }

  /**
   * Legacy row selection used key match (submission_id + key_id), not object
   * reference identity. This keeps selection stable after hierarchy refresh.
   */
  isEntitySelected(item: any): boolean {
    if (!item || !this.selectedEntity) {
      return false;
    }
    return String(item?.submission_id ?? '') === String(this.selectedEntity?.submission_id ?? '')
      && String(item?.key_id ?? '') === String(this.selectedEntity?.key_id ?? '');
  }

  private resolveBusinessType(entity: any): string {
    const rt = String(entity?.return_type ?? this.selectedEntity?.return_type ?? '').trim();
    return rt === '1065' ? 'P' : 'C';
  }

  private resolveKeyId(entity: any): any {
    const raw = entity?.le_type === 'HOO'
      ? (entity?.hoo_key_id ?? this.pdfParams?.keyId ?? entity?.key_id)
      : (entity?.key_id ?? this.pdfParams?.keyId);
    return raw ?? '';
  }

  private resolveSubmissionId(entity: any): any {
    const raw = entity?.le_type === 'HOO'
      ? (entity?.hoo_submission_id ?? this.pdfParams?.submission_id ?? entity?.submission_id)
      : (entity?.submission_id ?? this.pdfParams?.submission_id);
    return raw ?? '';
  }

  /**
   * Legacy API accepts `'null'` token for empty CDR/reporting period.
   */
  private nullableParam(v: any): string {
    if (v === null || v === undefined || String(v).trim() === '') {
      return 'null';
    }
    return String(v).trim();
  }

  private parseTaxForms(response: any): any[] | null {
    const taxForms = response?.taxForms;

    if (!Array.isArray(taxForms) || taxForms.length === 0) {
      return [];
    }

    const jsonString = taxForms[0]?.jsonString;
    if (!jsonString || typeof jsonString !== 'string') {
      return [];
    }

    try {
      const parsed = JSON.parse(jsonString);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing tax forms:', error);
      return null;
    }
  }

  loadPDF(pdf: any): void {
    this.showExpand = true;
    this.expandPdfView = false;
    this.isMerge = false;
    this.isLoadPdf = true;
    this.pdfLoaded = false;
    this.showSelectAll = this.showMerge = true;

    this.selectedForm = pdf;
    this.pdfParams.formName = this.selectedEntity.tax_year <= this.LEGACY_TAX_YEAR ? pdf.form_id : pdf.form_name;
    this.pdfParams.wpIrsFormNo = pdf.irs_form || '';
    this.pdfParams.s3_url = pdf.s3_url || '';
    this.pdfParams.formSubCat = pdf.form_sub_cat;
    this.pdfParams.formSub = pdf.subform || '';
    this.pdfParams.parentLeId = '';
    this.pdfParams.line = pdf.line_no || '';
    this.pdfParams.schedule = pdf.schedule || '';
    this.pdfParams.partNo = pdf.part_no || '';
    this.pdfParams.ein = pdf.ein || '';
    this.pdfParams.is_attachment = pdf.is_attachment || 0;
    this.pdfParams.is_statement = pdf.is_whitepaper == 2 ? 1 : 0;
    this.pdfParams.jcdKey = this.JCD_KEY;

    if (this.selectedEntity.consol_type === 'PSHIP' && this.pdfParams.keyId === 0) {
      this.pdfParams.keyId = pdf.key_id;
    }

    this.pdfParams.contSrc = this.SRC;
    this.pdfParams.batchId = this.selectedEntity.batchId;
    this.pdfParams.srcType = 'DOM';
    this.pdfParams.mergeAll = false;
    this.pdfParams.pdfToBeMerged = false;

    this.cacheService.saveSelectedForm(this.isState, pdf);
    this.cacheService.savePdfParams(this.isState, this.pdfParams);

    this.fetchPdf();
  }

  loadMergedPDF(): void {
    this.showExpand = true;
    this.expandPdfView = false;
    this.isMerge = false;
    this.isLoadPdf = true;
    this.pdfLoaded = false;
    this.pdfParams.pdfToBeMerged = true;

    const selectedPdfs = this.getSelectedPDFs();
    this.pdfParams.formName = this.getJoinedStringBy(selectedPdfs, this.selectedEntity.tax_year <= this.LEGACY_TAX_YEAR ? 'form_id' : 'form_name');
    this.pdfParams.wpIrsFormNo = this.getJoinedStringBy(selectedPdfs, 'irs_form');
    this.pdfParams.formSubCat = this.getJoinedStringBy(selectedPdfs, 'form_sub_cat');
    this.pdfParams.formSub = this.getJoinedStringBy(selectedPdfs, 'subform');
    this.pdfParams.parentLeId = '';
    this.pdfParams.line = this.getJoinedStringBy(selectedPdfs, 'line_no');
    this.pdfParams.schedule = this.getJoinedStringBy(selectedPdfs, 'schedule');
    this.pdfParams.partNo = this.getJoinedStringBy(selectedPdfs, 'part_no');
    this.pdfParams.ein = this.getJoinedStringBy(selectedPdfs, 'ein');
    this.pdfParams.is_attachment = this.getJoinedStringBy(selectedPdfs, 'is_attachment');
    this.pdfParams.jcdKey = this.JCD_KEY;

    const temp = this.getJoinedStringBy(selectedPdfs, 'is_whitepaper');
    this.pdfParams.is_statement = temp.map((i: any) => i == '2' ? '1' : '0');

    this.pdfParams.mergeAll = this.pdfs.length === selectedPdfs.length;
    this.pdfParams.contSrc = this.SRC;
    this.pdfParams.batchId = this.selectedEntity.batchId;
    this.pdfParams.srcType = 'DOM';

    this.cacheService.savePdfParams(this.isState, this.pdfParams);

    this.fetchPdf();
  }

  private fetchPdf(): void {
    this.pdfLoading = true;
    this.pdfUrl = null;
    this.formNotAvail = false;
    this.pdfParams.withDrillDowns = '1';
    this.cdr.detectChanges();

    const obs = this.pdfParams.pdfToBeMerged
      ? this.formViewService.mergePdf(this.pdfEngineUrl, this.pdfParams)
      : this.formViewService.getPdf(this.pdfEngineUrl, this.pdfParams);

    obs.subscribe(
      (data: ArrayBuffer) => {
        this.pdfLoading = false;
        if (data.byteLength === 0) {
          this.formNotAvail = true;
          this.cacheService.savePdfBlob(this.isState, null, null);
        } else {
          const blob = new Blob([data], { type: 'application/pdf' });
          const objectUrl = URL.createObjectURL(blob);
          // Parity with legacy `viewPdf` (`formViewDirectives.js`): hide embedded
          // PDF viewer chrome (toolbar / nav panes) in the iframe.
          const url = `${objectUrl}#toolbar=0&navpanes=0`;
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          // Legacy `viewPdf` saved the rendered blob URL on
          // `FormViewFactory.formViewCache.pdfBlob` so returning to the screen
          // re-displays the same PDF without re-fetching.
          this.cacheService.savePdfBlob(this.isState, url, data);
        }
        this.pdfLoaded = true;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.pdfLoading = false;
        this.formNotAvail = true;
        this.pdfLoaded = true;
        this.cacheService.savePdfBlob(this.isState, null, null);
        this.cdr.detectChanges();
      }
    );
  }

  selectForm(): void {
    this.enableMerge = this.getSelectedPDFs().length > 1;
    this.cdr.detectChanges();
  }

  selectAllPDFs(): void {
    this.pdfs.forEach(pdf => {
      if (pdf.isDisplay) {
        pdf.checked = !this.isSelectAll;
      }
    });
    this.isSelectAll = !this.isSelectAll;
    this.enableMerge = this.getSelectedPDFs().length > 1;
    this.cdr.detectChanges();
  }

  formFilter(pdf: any): boolean {
    if (this.formSearchTerm.length > 0) {
      pdf.isDisplay = pdf.checked || pdf.label?.toString().toLowerCase().includes(this.formSearchTerm.toLowerCase());
      return pdf.isDisplay;
    }
    pdf.isDisplay = true;
    return true;
  }

  private getSelectedPDFs(): any[] {
    return this.pdfs.filter(pdf => pdf.checked);
  }

  private getJoinedStringBy(arr: any[], prop: string): any[] {
    return arr.map(item => item[prop] ? item[prop] : '');
  }

  /**
   * Legacy `runAction('expand')` / `runAction('download')` →
   * `$scope.$broadcast('pdf:run-action', action)` → `viewPdf.loadPdf(action)`
   * (`formViewDirectives.js` L78-122): re-fetch `/fillForm` with
   * `withDrillDowns = '0'`, then either `window.open(blobUrl, '_blank')`
   * (expand) or trigger a download. Both actions MUST go through the
   * non-drilldown PDF — the inline iframe blob (drilldowns enabled) is
   * not the right artifact to hand the user.
   */
  private fetchPdfForAction(action: 'expand' | 'download'): void {
    if (this.runningAction || !this.pdfEngineUrl) {
      return;
    }
    this.runningAction = true;
    this.cdr.detectChanges();

    const prevDrill = this.pdfParams.withDrillDowns ?? '1';
    this.pdfParams.withDrillDowns = '0';

    const obs = this.pdfParams.pdfToBeMerged
      ? this.formViewService.mergePdf(this.pdfEngineUrl, this.pdfParams)
      : this.formViewService.getPdf(this.pdfEngineUrl, this.pdfParams);

    obs.subscribe(
      (data: ArrayBuffer) => {
        this.pdfParams.withDrillDowns = prevDrill;
        this.runningAction = false;
        if (!data || data.byteLength === 0) {
          const msg = action === 'download'
            ? 'No PDF data available to download.'
            : 'No PDF data available to open.';
          this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--yellow');
        } else if (action === 'expand') {
          const blob = new Blob([data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
        } else {
          this.downloadPdfFromArrayBuffer(data);
        }
        this.cdr.detectChanges();
      },
      () => {
        this.pdfParams.withDrillDowns = prevDrill;
        this.runningAction = false;
        const msg = action === 'download'
          ? 'Something went wrong downloading the PDF.'
          : 'Something went wrong opening the PDF.';
        this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Legacy filename format from `formViewDirectives.js` `downloadPdf()`:
   * `${taxYear}_${returnType}_${leName}(${leId})_${leType.includes('HO') ? leType + '_' : ''}${formName}.pdf`.
   */
  private downloadPdfFromArrayBuffer(data: ArrayBuffer): void {
    const blob = new Blob([data], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);

    const returnTypeCode = String(this.pdfParams?.returnType ?? this.selectedEntity?.return_type ?? '');
    const returnType = returnTypeCode === '1120'
      ? 'Corporate'
      : returnTypeCode === '1065'
        ? 'Partnership'
        : 'Extension';
    const leTypeRaw = String(this.pdfParams?.leType ?? this.selectedEntity?.le_type ?? '');
    const leTypePrefix = leTypeRaw.includes('HO') ? `${leTypeRaw}_` : '';
    const taxYear = this.pdfParams?.taxYear ?? this.selectedEntity?.tax_year ?? '';
    const leName = this.pdfParams?.leName ?? this.selectedEntity?.name ?? '';
    const leId = this.pdfParams?.leId ?? this.selectedEntity?.leid ?? '';
    const formName = this.pdfParams?.formName ?? this.selectedForm?.form_name ?? 'form';

    link.download = `${taxYear}_${returnType}_${leName}(${leId})_${leTypePrefix}${formName}.pdf`;
    link.click();
    link.remove();
  }

  runAction(action: string): void {
    if (action === 'xml' || action === 'zip') {
      this.downloadXMLorZIP(action);
      return;
    }
    if (action === 'expand') {
      this.fetchPdfForAction('expand');
      return;
    }
    if (action === 'download') {
      this.fetchPdfForAction('download');
    }
  }

  private downloadXMLorZIP(type: string): void {
    if (!this.selectedEntity) return;
    this.runningAction = true;
    this.cdr.detectChanges();

    this.formViewService.downloadXMLorZIP(
      this.bulkPdfUrl, this.selectedEntity.submission_id,
      this.selectedEntity.key_id, this.selectedEntity.le_type, type
    ).subscribe(
      (data: ArrayBuffer) => {
        this.runningAction = false;
        if (data.byteLength === 0) {
          this.gtwSnackbarService.openSnackBar('No file available to download', 'gtw-snackbar--yellow');
          this.cdr.detectChanges();
          return;
        }

        const blob = new Blob([data], { type: 'application/' + type });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const returnType = this.selectedEntity.return_type === '1120' ? 'Corporate'
          : this.selectedEntity.return_type === '1065' ? 'Partnership' : 'Extension';
        const leTypeSuffix = this.selectedEntity.le_type.includes('HO') ? this.selectedEntity.le_type : '';
        link.download = `${this.selectedEntity.tax_year}_${returnType}_${this.selectedEntity.name}(${this.selectedEntity.leid})_${leTypeSuffix}.${type}`;
        link.click();
        link.remove();
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.runningAction = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  pushPdfsToS3(): void {
    this.disableS3PushButton = true;
    this.cdr.detectChanges();

    this.formViewService.pushPdfsToS3(
      this.pdfEngineUrl, this.getTaxYearValue(),
      this.getScenarioValue(), this.JCD_KEY
    ).subscribe(
      () => { this.disableS3PushButton = false; this.cdr.detectChanges(); },
      () => { this.disableS3PushButton = false; this.cdr.detectChanges(); }
    );
  }

  updateEntityS3Status(): void {
    if (!this.selectedEntity) return;
    this.disableS3UpdateStatus = true;
    this.cdr.detectChanges();

    this.formViewService.updateS3Status(
      this.pdfEngineUrl, this.getTaxYearValue(),
      this.selectedEntity.leid, this.selectedEntity.le_type,
      this.pdfParams.keyId, this.pdfParams.submission_id
    ).subscribe(
      (response: any) => {
        this.disableS3UpdateStatus = false;
        if (response?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar(response.message || 'S3 status updated', 'gtw-snackbar--green');
        }
        this.cdr.detectChanges();
      },
      () => { this.disableS3UpdateStatus = false; this.cdr.detectChanges(); }
    );
  }

  createSnapshot(ignored = false): void {
    if (!this.selectedEntity) {
      this.gtwSnackbarService.openSnackBar('Please select an entity!', 'gtw-snackbar--yellow');
      return;
    }

    this.formViewService.createSnapshot(
      this.bulkPdfUrl, this.selectedEntity.submission_id,
      this.getTaxYearValue(), this.getScenarioValue(),
      this.JCD_KEY, ignored
    ).subscribe();
  }

  private loadIRSId(): void {
    if (!this.selectedEntity?.submission_id || !this.baseUrl) return;

    this.formViewService.loadIRSId(this.baseUrl, this.selectedEntity.submission_id).subscribe(
      (data: any) => {
        this.irsSubmissionId = data?.jsonObject?.[0]?.IRS_SUBMISSION_ID || null;
        this.cdr.detectChanges();
      },
      () => { this.irsSubmissionId = null; this.cdr.detectChanges(); }
    );
  }

  // Tree utility methods
  toggleNode(item: any): void {
    if (item.is_leaf === 1) return;

    const tree = this.entityTree;
    const index = tree.indexOf(item) + 1;

    if (!item.childrenHidden) {
      item.childrenHidden = true;
      for (let i = index; i < tree.length; i++) {
        if (tree[i].lvl <= item.lvl) return;
        tree[i].hidden = true;
        if (i > 0 && tree[i - 1].lvl < tree[i].lvl) {
          tree[i - 1].childrenHidden = true;
        }
      }
    } else {
      item.childrenHidden = false;
      for (let i = index; i < tree.length; i++) {
        if (tree[i].lvl <= item.lvl) return;
        if (tree[i].lvl - 1 === item.lvl) {
          tree[i].hidden = false;
        } else {
          tree[i].hidden = true;
        }
      }
    }
    this.cdr.detectChanges();
  }

  visibleNodes(node: any): boolean {
    return !node.hidden;
  }

  /** True when the inline PDF viewer panel is shown (same as `pdf-container` *ngIf). */
  isPdfViewerOpen(): boolean {
    return this.isLoadPdf && !this.expandPdfView;
  }

  closeTreePane(): void {
    this.treePaneMinimized = true;
    this.cdr.markForCheck();
  }

  openTreePane(): void {
    this.treePaneMinimized = false;
    this.cdr.markForCheck();
  }

  /** Native title only when the label is truncated (ellipsis). */
  onFormLabelMouseEnter(event: MouseEvent, label: string | undefined): void {
    const el = event.currentTarget as HTMLElement;
    const text = (label ?? '').toString();
    if (!text) {
      el.removeAttribute('title');
      return;
    }
    if (el.scrollWidth > el.clientWidth) {
      el.setAttribute('title', text);
    } else {
      el.removeAttribute('title');
    }
  }

  expandCollapseAll(): void {
    this.expandAll = !this.expandAll;
    if (!this.expandAll) {
      this.expandAllTxt = 'Expand All';
      this.entityTree?.forEach(row => {
        row.childrenHidden = true;
        if (row.lvl !== 0) row.hidden = true;
      });
    } else {
      this.expandAllTxt = 'Collapse All';
      this.entityTree?.forEach(row => {
        row.childrenHidden = false;
        row.hidden = false;
      });
    }
    this.cdr.detectChanges();
  }

  filterTreeData(): void {
    const searchLabel = this.treeType === 'BSLA' ? 'me_label' : 'name';

    if (!this.searchTerm || this.searchTerm.length === 0) {
      this.entityTree = _.cloneDeep(this.entityTreeCopy);
      this.entityTree.forEach(node => { node.hidden = false; node.childrenHidden = false; });
      // Match legacy clearHiddenFlag: full tree visible → same as expanded; keep icon in sync.
      this.expandAll = true;
      this.expandAllTxt = 'Collapse All';
      this.cdr.detectChanges();
      return;
    }

    const treeDataSet = new Set<any>();
    const parentNodesAhead = this.entityTreeCopy.filter(item => !item.is_leaf);

    for (let i = 0; i < this.entityTreeCopy.length; i++) {
      const node = this.entityTreeCopy[i];
      node.hidden = false;
      node.childrenHidden = false;

      const searchStr = ((node[searchLabel] || '') + (node['leid'] || '') + (node['cdr_no'] || '') + (node['reporting_period'] || '')).toLowerCase();

      if (searchStr.includes(this.searchTerm.toLowerCase())) {
        const pathNodes = (node.path || '').split('/');
        if (pathNodes.length > 2) {
          pathNodes.slice(1, -1).forEach((pn: string) => {
            const parentNode = parentNodesAhead.find(item => (item[searchLabel] || '').includes(pn.trim()));
            if (parentNode) treeDataSet.add(parentNode);
          });
        }
        treeDataSet.add(node);
        if (!node.is_leaf) {
          while (i + 1 < this.entityTreeCopy.length && this.entityTreeCopy[i + 1].lvl > node.lvl) {
            i++;
            treeDataSet.add(this.entityTreeCopy[i]);
          }
        }
      }
    }

    this.entityTree = Array.from(treeDataSet);
    this.cdr.detectChanges();
  }

  getRecordsCount(name: string, parent?: string, consolGroupKey?: string): number {
    if (!this.entityTree || this.entityTree.length === 0 || this.entityTree[0].noDataFlag) return 0;

    if (name) {
      return this.entityTree.filter(item =>
        parent
          ? item.parent === name && item.path?.includes('/ ' + parent + '/ ' + name) && item.consol_group_key === consolGroupKey
          : item.parent === name
      ).length;
    } else if (this.treeType === 'BSLA') {
      return this.entityTree.filter(item => true).length;
    } else {
      return this.entityTree.filter(item =>
        ((item.external_y_n === 'Y') || (item.cdr_no !== null && item.reporting_period !== null && item.le_name !== null)) && item.is_entity
      ).length;
    }
  }

  /**
   * Legacy `form-view.html` tree anchor text (two interpolations with a space between):
   * `item.name + (item.is_entity ? '(' + leid + '/' + cdr_no + '/' + reporting_period + ')' : '')`
   * ` + (!item.is_leaf && treeType !== 'BSLA' ? '(' + getRecordsCount(name, parent, consol_group_key) + ')' : '')`
   */
  getTreeNodeDisplayLabel(item: any): string {
    const name = (item?.name ?? item?.me_label ?? '').toString();
    let text = name;
    if (this.isEntityTreeRow(item)) {
      const leid = item.leid ?? '';
      const cdr = item.cdr_no ?? '';
      const rp = item.reporting_period ?? '';
      text += `(${leid}/${cdr}/${rp})`;
    }
    const isLeaf =
      item?.is_leaf === 1 || item?.is_leaf === '1' || item?.is_leaf === true;
    if (!isLeaf && this.treeType !== 'BSLA') {
      text += ` (${this.getRecordsCount(item?.name, item?.parent, item?.consol_group_key)})`;
    }
    return text;
  }

  /**
   * Legacy `uib-tooltip`: name + entity triple only (no folder record count).
   */
  getTreeNodeTooltip(item: any): string {
    const name = (item?.name ?? item?.me_label ?? '').toString();
    let text = name;
    if (this.isEntityTreeRow(item)) {
      text += `(${item.leid ?? ''}/${item.cdr_no ?? ''}/${item.reporting_period ?? ''})`;
    }
    return text;
  }

  /** Rows currently visible in the sidebar tree (respects expand/collapse and search). */
  getVisibleTreeRowCount(): number {
    if (!this.entityTree?.length || this.entityTree[0]?.noDataFlag) {
      return 0;
    }
    return this.entityTree.filter(n => !n.hidden).length;
  }

  /** True when the form tile should show `pdf.png` instead of text. */
  isPdfFileBadge(pdf: any): boolean {
    return this.getFormFileBadge(pdf) === 'PDF';
  }

  /** True when the tile uses `wpd.png` as background (legacy form-view.html pdf-img / wpd-img). */
  isWpdFileBadge(pdf: any): boolean {
    return this.getFormFileBadge(pdf) === 'WPD';
  }

  /** Attachment / binary tile uses `assets/img/efile-att.png` (legacy `atchmt-img-bg`). */
  isBinFileBadge(pdf: any): boolean {
    return this.getFormFileBadge(pdf) === 'BIN';
  }

  /**
   * Badge for form grid tiles — aligned with legacy `form-view.html`:
   * `wpd-img-bg`: is_whitepaper && !is_attachment; `pdf-img-bg`: !is_whitepaper && !is_attachment; attachments separate.
   */
  getFormFileBadge(pdf: any): string {
    const isAttachment = !!pdf?.is_attachment;
    const isWhitepaper = !!pdf?.is_whitepaper;
    if (isWhitepaper && !isAttachment) {
      return 'WPD';
    }
    if (!isWhitepaper && isAttachment) {
      return 'BIN';
    }
    const label = (pdf?.label || '').toString().toUpperCase();
    const sub = (pdf?.form_sub_cat || '').toString().toUpperCase();
    if (label.includes('.WPD') || sub.includes('WPD')) {
      return 'WPD';
    }
    if (isAttachment || sub.includes('BINARY')) {
      return 'BIN';
    }
    return 'PDF';
  }

  /**
   * Legacy `form-view.html` tree label:
   * `item.is_entity || treeType === 'BSLA' ? (consol_type !== 'SBS' || isState) && entitySelected(item) : toggleNode(item)`
   */
  onTreeLabelClick(item: any): void {
    if (this.rowOpensEntityForms(item)) {
      if (item?.consol_type !== 'SBS' || this.isState) {
        this.entitySelected(item);
      }
    } else {
      this.toggleNode(item);
    }
  }

  /**
   * Legacy: `vs-list-item[data-parent*="Subsidiaries"|"Parent"][data-is-entity="1"][data-is-gtw-view="true"]`
   * hover reveals the All HO / HO Only actions. Only shown for gtw view (non-state)
   * entity rows that sit under Subsidiaries or Parent branches.
   */
  shouldShowSubsidiaryActions(item: any): boolean {
    if (this.isState) {
      return false;
    }
    if (!this.isEntityTreeRow(item)) {
      return false;
    }
    const parent = (item?.parent || '').toString();
    return parent.indexOf('Subsidiaries') >= 0 || parent.indexOf('Parent') >= 0;
  }

  /**
   * Legacy:
   * `ctrl.selectedSubsidiaryType = 'All HO' + item.ein; item.le_type = 'HO'; ctrl.entitySelected(item);`
   * `ctrl.selectedSubsidiaryType = 'HO Only' + item.ein; item.le_type = 'HOO'; ctrl.entitySelected(item);`
   */
  selectSubsidiaryType(item: any, type: 'HO' | 'HOO', evt?: Event): void {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }
    const ein = item?.ein ?? '';
    this.selectedSubsidiaryType = (type === 'HO' ? 'All HO' : 'HO Only') + ein;
    item.le_type = type;
    this.entitySelected(item);
    this.cdr.detectChanges();
  }

  /** True when the "All HO" button should render as active. */
  isAllHoActive(item: any): boolean {
    const ein = item?.ein ?? '';
    return this.selectedSubsidiaryType === 'All HO' + ein;
  }

  private rowOpensEntityForms(item: any): boolean {
    return this.isEntityTreeRow(item) || this.treeType === 'BSLA';
  }

  /** Treat API `is_entity` as legacy AngularJS (0/1, Y/N, boolean). */
  private isEntityTreeRow(item: any): boolean {
    const v = item?.is_entity;
    if (v === 0 || v === '0' || v === false || v === 'N' || v === 'n') {
      return false;
    }
    return v === 1 || v === '1' || v === true || v === 'Y' || v === 'y' || !!v;
  }

  getIconClass(item: any): string {
    if (item.noEntities) return 'fa-exclamation-circle';
    if (item.is_leaf == 1 && item.is_entity) {
      if (item.is_accepted === 'Y') return 'fa fa-check toggle';
      if (item.is_locked === 'Y') return 'fa fa-lock toggle';
      if (item.external_y_n === 'Y') return 'fa fa-globe toggle';
      return 'fa fa-file-pdf-o toggle';
    }
    return item.childrenHidden
      ? 'toggle fa hover-override fa-folder'
      : 'toggle fa hover-override fa-folder-open';
  }

  /**
   * Legacy: `ng-style="{color: item.lvl > 0 ? (currentTheme === 'theme-white' ? '#c25423' : '#cd5763') : '#000000'}"`.
   * Drives the color of the tree `<i class="toggle fa …">` icon (including `fa-file-pdf-o`).
   */
  getTreeIconColor(item: any): string {
    const lvl = Number(item?.lvl || 0);
    if (lvl > 0) {
      return this.currentTheme === 'theme-white' ? '#c25423' : '#cd5763';
    }
    return '#000000';
  }

  private sanitizeTree(): void {
    for (let index = 0; index < 4; index++) {
      this.entityTree = this.entityTree.filter(item =>
        this.getRecordsCount(item.name, item.parent, item.consol_group_key) > 0 || (item.is_leaf && item.is_entity)
      );
    }
  }

  getTreeName(): string {
    const found = this.treeTypeTabs.find(t => t.value === this.treeType);
    return found ? found.name : '';
  }

  refresh(): void {
    this.requestGridUpdate.emit('refresh');
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  trackByFn(index: number, item: any): any {
    return item?.name || item?.me_label || index;
  }

  trackByLabelFn(index: number, item: any): string {
    return item?.label || index;
  }

  toggleExpandPdf(): void {
    this.expandPdfView = true;
    this.cdr.detectChanges();
  }

  // Bulk Export
  openBulkExportPopUp(): void {
    this.showBulkExport = true;
    this.cdr.detectChanges();
  }

  closeBulkExport(): void {
    this.showBulkExport = false;
    this.cdr.detectChanges();
  }

  onOpenDiagnostics(event: any): void {
    this.showReviewDiagnostics = true;
    this.reviewDiagParams = event;
    this.cdr.detectChanges();
  }

  onDiagnosticsDismissed(): void {
    this.showReviewDiagnostics = false;
    this.cdr.detectChanges();
  }

  onDiagnosticsCompleted(ignored: boolean): void {
    this.showReviewDiagnostics = false;
    if (this.reviewDiagParams?.onComplete) {
      this.reviewDiagParams.onComplete(ignored);
    }
    this.cdr.detectChanges();
  }

  onNavigateToDiagnostics(event: any): void {
    // Legacy parity: review-diagnostics goToDiagnostics() (formViewCtrl.js
    // L1756-1775) first broadcasts 'close-all-modals' which dismisses both the
    // Review Diagnostics modal AND the bulk-export modal stacked behind it
    // (GlobalControllers.js L125-129 -> $uibModalStack.dismissAll()), then
    // broadcasts 'navigate-to-diagnostics' which is delegated to
    // workspaceFactory.loadLink('link', '{}', activity_key, screen_key, filters)
    // (GlobalControllers.js L132-135).
    this.showReviewDiagnostics = false;
    this.showBulkExport = false;
    this.cdr.detectChanges();

    if (!event || event.activity_key == null || event.screen_key == null) {
      return;
    }

    // Legacy used setTimeout(..., 100) before navigate so modals finish closing
    // before workspace switches (formViewCtrl.js L1761-1774).
    setTimeout(() => {
      this.workspaceService.loadLink(
        'link', '{}',
        event.activity_key, event.screen_key,
        event.filters
      );

      // Form View runs on standalone routes (e.g. /form-view/dashboard).
      // loadLink updates workspace state and breadcrumbs, but the URL only
      // changes when we navigate into the workspace shell (same pattern as
      // openBulkExportHistory / navigateToPdfFormsWorkspaceScreen).
      const active = this.workspaceService.workspaceFactory?.activeScreen;
      const slug = active ? this.workspaceService.getScreenSlug(active) : '';
      const isModulesHome =
        String(active?.screen_key) === '10906' || active?.link === 'workspace.modules';
      if (slug && !isModulesHome) {
        // Helper accounts for top-level legacy states that live OUTSIDE
        // `/workspace/` — see WorkspaceService.getScreenNavigationCommands.
        const commands = this.workspaceService.getScreenNavigationCommands(active);
        const clientKey = sessionStorage.getItem('client_key') || '0';
        this.router.navigate(commands, {
          queryParams: { client_key: clientKey },
        });
      }

      this.cdr.markForCheck();
    }, 100);
  }

  // Download XML/ZIP by IRS Submission ID
  downloadXMLorZIPBySubmission(irsSubmissionId: string, type: string): void {
    this.runningAction = true;
    this.cdr.detectChanges();

    this.formViewService.downloadXMLorZIP(
      this.bulkPdfUrl, irsSubmissionId, '', '', type
    ).subscribe(
      (data: ArrayBuffer) => {
        this.runningAction = false;
        if (data.byteLength > 0) {
          const ext = type === 'xml' ? 'xml' : 'zip';
          const prefix = type === 'xml' ? 'xml_' : '';
          const blob = new Blob([data], { type: 'application/' + ext });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `${prefix}${irsSubmissionId}.${ext}`;
          link.click();
          link.remove();
        } else {
          this.gtwSnackbarService.openSnackBar('No file available to download', 'gtw-snackbar--yellow');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.runningAction = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  // Download with snapshot review
  downloadWithSnapshot(): void {
    if (!this.selectedEntity) return;
    this.createSnapshot(false);
    this.runAction('download');
  }

  /**
   * Legacy `formViewCtrl.js` `getProccessList()` (L405-410):
   *   FormViewFactory.getDomProcessList()
   *     .then(response => alert(JSON.stringify(response.data)));
   *
   * The "Show DOM queue" tile triggers `GET /pdf-engine/getDomProcessingList`
   * and renders the raw response in a native browser `alert()` so the support
   * team can copy/paste the queue contents. Keep this behavior in the migrated
   * component for parity — a snackbar + console log silently dropped the data
   * the support flow depends on.
   */
  getProcessList(): void {
    this.formViewService.getDomProcessList(this.pdfEngineUrl).subscribe(
      (data: any) => {
        window.alert(JSON.stringify(data));
      },
      () => {
        this.gtwSnackbarService.openSnackBar('Failed to load process list', 'gtw-snackbar--red');
      }
    );
  }

  /**
   * Establish the STOMP-over-SockJS connection and subscribe to the
   * form-view notification channel. Mirrors the legacy AngularJS
   * establishWebSocketConnection() in formViewCtrl.js.
   */
  private establishWebSocketConnection(): void {
    const customApiUrl: string = this.GENERAL_CONFIG?.custom_api_url
      || this.GENERAL_CONFIG?.custom
      || '';
    const ssoId: string = this.USER_SETTINGS?.user?.sso_id || '';
    if (!customApiUrl || !ssoId) { return; }

    const endpoint = customApiUrl + this.WEBSOCKET_ENDPOINT;
    const channel = this.WEBSOCKET_CHANNEL + ssoId;

    this.wsEndpointUrl = endpoint;
    this.wsChannel = channel;

    this.stompMessageService.establishConnection(endpoint, () => {
      this.stompMessageService.subscribeToChannelWithCallback(
        endpoint,
        channel,
        (message) => {
          try {
            const notification = JSON.parse(message.body);
            this.handleWebSocketMessage(notification);
          } catch (err) {
            console.error('[FormView] failed to parse websocket payload', err, message?.body);
          }
        }
      );
    });
  }

  /**
   * Re-establish the websocket if it has dropped. Mirrors the legacy
   * reEstablishWebsocketConnection() in formViewCtrl.js — @stomp/stompjs
   * auto-reconnects via `reconnectDelay`, but callers may force a retry.
   */
  private reEstablishWebsocketConnection(): void {
    if (!this.wsEndpointUrl) { return; }
    const handle = this.stompMessageService.getStompClient(this.wsEndpointUrl);
    if (!handle || handle.state === 'disconnected') {
      this.establishWebSocketConnection();
    }
  }

  // WebSocket message handling
  handleWebSocketMessage(notification: any): void {
    if (this.selectedEntity
      && (notification.keyId + '' === this.selectedEntity.key_id + ''
        || notification.keyId + '' === this.selectedEntity.hoo_key_id + '')
      && notification.leType === this.selectedEntity.le_type
      && (notification.submissionId + '' === this.selectedEntity.submission_id + ''
        || notification.submissionId + '' === this.selectedEntity.hoo_submission_id + '')) {

      this.messageTxt = notification.message;

      if (notification.requestStatus === 'COMPLETE') {
        this.messageComplete = true;
        this.messageWarn = false;

        if (this.messageTxt?.includes('New PDFs are ready')) {
          this.getForms(this.selectedEntity, 'Y');
          if (this.pdfLoaded) {
            this.pdfParams.pdfToBeMerged ? this.loadMergedPDF() : this.loadPDF(this.selectedForm);
          }
        }

        this.completeTimer = setTimeout(() => {
          this.messageComplete = false;
          this.messageTxt = '';
          this.cdr.detectChanges();
        }, 20000);
      } else if (notification.requestStatus === 'NO_CHANGE' || notification.requestStatus === 'FAILED') {
        this.messageComplete = false;
        this.messageWarn = true;
        this.completeTimer = setTimeout(() => {
          this.messageWarn = false;
          this.messageTxt = '';
          this.cdr.detectChanges();
        }, 20000);
      } else {
        if (this.completeTimer) {
          clearTimeout(this.completeTimer);
        }
      }

      this.cdr.detectChanges();
    }
  }

  openDrilldown(drilldownData: any): void {
    const tabCode = this.drilldownRouting.buildTabCode(
      drilldownData?.leName,
      drilldownData?.elementName
    );
    this.showDrilldown = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      if (!this.drilldownGrid) {
        return;
      }
      const existing = this.drilldownGrid.tabs.find(t => t.code === tabCode);
      if (existing) {
        this.gtwSnackbarService.openSnackBar('Tab is already open', 'gtw-snackbar--yellow');
        this.drilldownGrid.activateTab(existing);
      } else {
        this.drilldownGrid.addTab(drilldownData);
      }
      this.persistDrilldownTabs();
      this.navigateToElementViewTab(tabCode);
      this.onDrilldownWorkspaceChange();
    }, 50);
  }

  onDrilldownClosed(): void {
    this.showDrilldown = false;
    this.navigateToFormViewDashboard();
    this.onDrilldownWorkspaceChange();
  }

  onDrilldownGridFormCallback(event: AdminGridCallbackEvent): void {
    if (event.type === 'form' && event.data?.rowData) {
      console.log('Drilldown grid form callback received — row modal requested:', event);
    }
  }
}
